<template>
  <div>
    <page-header :title="getUserNameOrId">
      <template #actions>
        <account-settings
          :user="user"
          :isAdminOrRoot="isAdminOrRoot"
          :canEditAnyUsers="canEditAnyUsers"
          @updateUser="handleUpdateUser"
        />

        <v-menu
          bottom
          transition="scale-transition"
        >
          <template v-slot:activator="{ props }">
            <v-btn-primary
              class="mt-2 mb-2"
              v-bind="props"
              @click="downloadReports"
            >
              <v-icon class="mr-2" left>far fa-download</v-icon>
              Downloads
            </v-btn-primary>
          </template>
          <v-list>
            <v-list-item data-pw="downloadSubjectChoiceDialogButton"
                         @click="showDownloadSubjectChoiceReportDialog = true">
              {{ $t("components.UserDetailPage.menu.download_subject_report") }}
            </v-list-item>
            <v-list-item data-pw="downloadCVDialogButton" @click="showDownloadCVDialog = true">
              {{ $t("components.UserDetailPage.menu.download_cv") }}<br/>
            </v-list-item>
            <v-list-item data-pw="downloadAssessmentDialogButton" @click="showDownloadAssessmentReportDialog = true">
              {{ $t("components.UserDetailPage.menu.download_assessment_report") }} <br/>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </page-header>

    <v-container class="mt-0 mx-auto pa-0 border-b" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="overview">
          Overview
        </v-tab>
        <v-tab value="assessments">
          Assessments
        </v-tab>
        <v-tab value="cv">
          CV
        </v-tab>
        <v-tab value="choices">
          Choices
        </v-tab>
        <v-tab value="manage">
          Manage
        </v-tab>
      </v-tabs>
    </v-container>

    <div v-if="user" class="mt-2">
      <v-container class="mx-auto" fluid>

        <v-row v-if="user?.auth?.accountDisabled">
          <v-col cols="12">
            <v-alert type="warning">Account is disabled</v-alert>
          </v-col>
        </v-row>

        <component
          :is="currentTabComponent"
          :user="user"
          @updateUser="handleUpdateUser"
        />
      </v-container>
    </div>

    <v-container
      v-if="user"
      fluid class="py-4"
    >
      <v-dialog v-model="showDownloadSubjectChoiceReportDialog" persistent max-width="500">
        <download-subject-choice-dialog
          :user="user"
          @close="showDownloadSubjectChoiceReportDialog = false"
        />
      </v-dialog>
      <v-dialog v-model="showDownloadAssessmentReportDialog" persistent max-width="500">
        <download-assessment-dialog
          :user="user"
          @close="showDownloadAssessmentReportDialog = false"
        />
      </v-dialog>
      <v-dialog v-model="showDownloadCVDialog" persistent max-width="500">
        <download-cv-dialog
          :user="user"
          @close="showDownloadCVDialog = false"
        />
      </v-dialog>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref, watch, computed, defineAsyncComponent} from "vue";
import {useRoute, useRouter} from "vue-router";
import {
  isYapiError,
  DashUser,
  YapiError, Role, Entity, Action
} from "@YenzaCT/sdk";
import {useGlobalStore} from "@/store";
import yapi from "@/lib/yapi";
import DownloadSubjectChoiceDialog
  from "@/pages/Users/UserDetailPage/components/common/AccountSettingsMenu/DownloadSubjectChoiceCard.vue";
import DownloadAssessmentDialog
  from "@/pages/Users/UserDetailPage/components/common/AccountSettingsMenu/DownloadAssessmentCard.vue";
import DownloadCvDialog from "@/pages/Users/UserDetailPage/components/common/AccountSettingsMenu/DownloadCvCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import AccountSettings from "@/pages/Users/UserDetailPage/components/common/AccountSettingsMenu/AccountSettingsMenu.vue";

// Lazy load tab components
const OverviewTab = defineAsyncComponent(() => import("@/pages/Users/UserDetailPage/tabs/OverviewTab.vue"));
const AssessmentsTab = defineAsyncComponent(() => import("@/pages/Users/UserDetailPage/tabs/AssessmentsTab.vue"));
const CVTab = defineAsyncComponent(() => import("@/pages/Users/UserDetailPage/tabs/CVTab.vue"));
const ChoicesTab = defineAsyncComponent(() => import("@/pages/Users/UserDetailPage/tabs/ChoicesTab.vue"));
const ManageTab = defineAsyncComponent(() => import("@/pages/Users/UserDetailPage/tabs/ManageTab.vue"));

interface Props {
  userId: string;
  routeName?: string;
  tenantId?: string;
  institutionId?: string;
  cohortId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  routeName: "userDetail"
});

const store = useGlobalStore();
const route = useRoute();
const router = useRouter();
const showDownloadSubjectChoiceReportDialog = ref(false);
const showDownloadAssessmentReportDialog = ref(false);
const showDownloadCVDialog = ref(false);
const user = ref<DashUser>();
const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const canEditAnyUsers = computed(() => store.hasAbility(Entity.User, Action.edit));

// Map tab names to components
const tabComponents = {
  overview: OverviewTab,
  assessments: AssessmentsTab,
  cv: CVTab,
  choices: ChoicesTab,
  manage: ManageTab
};

// Get active tab from route, with router navigation
const activeTab = computed({
  get: () => (route.params.tab as string) || "overview",
  set: (value: string) => {
    const params: Record<string, string> = {
      tab: value.toLowerCase()
    };

    // Add context-specific params
    if (props.routeName === "tenantUserDetail") {
      params.tenantId = props.tenantId;
      params.userId = props.userId;
    } else if (props.routeName === "institutionUserDetail") {
      params.tenantId = props.tenantId;
      params.institutionId = props.institutionId;
      params.userId = props.userId;
    } else if (props.routeName === "cohortUserDetail") {
      params.tenantId = props.tenantId;
      params.institutionId = props.institutionId;
      params.cohortId = props.cohortId;
      params.userId = props.userId;
    } else {
      params.id = props.userId;
    }

    router.push({
      name: props.routeName,
      params
    });
  }
});

// Get the current tab component
const currentTabComponent = computed(() =>
  tabComponents[activeTab.value as keyof typeof tabComponents] || OverviewTab);

async function fetchUser() {
  try {
    store.networkBusy = true;
    user.value = (await yapi.admin.user.get(props.userId)).data;
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
}

const getUserNameOrId = computed(() => {
  if (user.value) {
    return user.value.profile.firstName || user.value.profile.lastName
      ? `${user.value.profile.firstName || ""} ${user.value.profile.lastName || ""}`.trim()
      : user.value._id;
  }
  return "";
});

function handleUpdateUser(success: boolean) {
  if (success) {
    fetchUser();
  }
}

function downloadReports() {
  // Implementation for download reports
}

onMounted(async () => {
  await fetchUser();
});

watch(() => props.userId, async (newId, oldId) => {
  if (newId !== oldId) {
    await fetchUser();
  }
});

</script>
